import * as React from "react";
import "./pdfEditor.css";

/**
 * Werkzeugleiste des PDF-Editors mit Reitern wie bei Microsoft (User
 * 02.10.2026) — reine Darstellung, der Zustand bleibt in `PdfEditorViewer`.
 * Ohne Fluent: eigenes HTML/CSS (`pdfEditor.css`), eigene Symbole (`pdfIcons`).
 *
 * Zwei Zeilen:
 *  1. die **Schnellzugriffszeile** (`schnell` links: Rückgängig, Wiederholen,
 *     Dateiname; `rechts`: Schließen, Speichern),
 *  2. die **Reiter** (`role="tablist"`), darunter die Werkzeuge des aktiven
 *     Reiters in Gruppen mit kleiner Überschrift.
 *
 * Darunter eine eigene Hinweiszeile (`data-werkzeug-hinweis`), damit Hinweise
 * die Leiste nie umbrechen. Unter 1100 px Leistenbreite zeigen die Knöpfe nur
 * ihr Symbol (Beschriftung in `title` und `aria-label`); gemessen wird die
 * Leiste selbst, nicht das Fenster.
 */

/** Unter dieser Leistenbreite nur Symbole. */
export const KOMPAKT_UNTER_PX = 1100;

const KompaktKontext = React.createContext(false);

/** Ob die umgebende Werkzeugleiste gerade nur Symbole zeigt. */
export const useWerkzeugKompakt = (): boolean => React.useContext(KompaktKontext);

/** Misst die Leiste selbst (nicht das Fenster) — unter `grenze` px zeigt sie nur Symbole. */
function useKompakt(
    rahmenRef: React.RefObject<HTMLDivElement | null>,
    vorgabe: boolean | undefined,
    grenze: number,
): boolean {
    const [gemessen, setGemessen] = React.useState(false);
    React.useLayoutEffect(() => {
        const el = rahmenRef.current;
        if (vorgabe !== undefined || !el) return;
        // Breite 0 = (noch) nicht gelayoutet, z. B. ausgeblendet — dann nichts ändern.
        const messen = (breite: number) => breite > 0 && setGemessen(breite < grenze);
        messen(el.getBoundingClientRect().width);
        if (typeof ResizeObserver === "undefined") return;
        const beobachter = new ResizeObserver((eintraege) => messen(eintraege[0]?.contentRect.width ?? 0));
        beobachter.observe(el);
        return () => beobachter.disconnect();
    }, [rahmenRef, vorgabe, grenze]);
    return vorgabe ?? gemessen;
}

export type ReiterDefinition = {
    key: string;
    label: string;
    /** Die Gruppen des Reiters (`WerkzeugGruppe`). */
    inhalt: React.ReactNode;
};

export const Werkzeugleiste: React.FC<{
    reiter: ReiterDefinition[];
    aktiv: string;
    onReiter: (key: string) => void;
    /** Nur Symbole zeigen? Ohne Angabe entscheidet die gemessene Breite der Leiste. */
    kompakt?: boolean;
    /** Schnellzugriff links (Rückgängig, Wiederholen, Dateiname). */
    schnell?: React.ReactNode;
    /** Rechts außen in der Schnellzugriffszeile — Schließen/Speichern. */
    rechts?: React.ReactNode;
    /** Eigene Zeile unter der Leiste — Fehler, Werkzeug-Hinweise, ungespeicherte Änderungen. */
    hinweis?: React.ReactNode;
    /** Ausgeblendet, aber gemountet (Seiten-Organisator bringt seine eigene Kopfzeile). */
    verborgen?: boolean;
}> = ({ reiter, aktiv, onReiter, kompakt: kompaktVorgabe, schnell, rechts, hinweis, verborgen }) => {
    const rahmenRef = React.useRef<HTMLDivElement | null>(null);
    const kompakt = useKompakt(rahmenRef, kompaktVorgabe, KOMPAKT_UNTER_PX);
    const aktiverReiter = reiter.find((r) => r.key === aktiv) ?? reiter[0];

    // Pfeiltasten wandern zwischen den Reitern (wie bei echten Tabs).
    const tastatur = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        e.preventDefault();
        const n = reiter.length;
        const ziel = reiter[(index + (e.key === "ArrowRight" ? 1 : n - 1)) % n];
        onReiter(ziel.key);
        e.currentTarget.parentElement?.querySelector<HTMLElement>(`[data-reiter="${ziel.key}"]`)?.focus();
    };

    return (
        <KompaktKontext.Provider value={kompakt}>
            <div ref={rahmenRef} className="pdfx pdfx-leiste" style={verborgen ? { display: "none" } : undefined}>
                <div className="pdfx-schnell" data-schnellzugriff="">
                    {schnell}
                    <span className="pdfx-spacer" />
                    {rechts}
                </div>
                <div className="pdfx-reiter" role="tablist" aria-label="Bereiche des PDF-Editors">
                    {reiter.map((r, i) => (
                        <button
                            key={r.key}
                            type="button"
                            role="tab"
                            data-reiter={r.key}
                            aria-selected={aktiverReiter?.key === r.key}
                            aria-controls={`pdfx-panel-${r.key}`}
                            tabIndex={aktiverReiter?.key === r.key ? 0 : -1}
                            onClick={() => onReiter(r.key)}
                            onKeyDown={(e) => tastatur(e, i)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
                {aktiverReiter && (
                    <div
                        className="pdfx-werkzeuge"
                        role="toolbar"
                        id={`pdfx-panel-${aktiverReiter.key}`}
                        aria-label={`Werkzeuge: ${aktiverReiter.label}`}
                    >
                        {aktiverReiter.inhalt}
                    </div>
                )}
                <div className="pdfx-hinweis" data-werkzeug-hinweis="">
                    {hinweis}
                </div>
            </div>
        </KompaktKontext.Provider>
    );
};

/** Eine Gruppe der Leiste: kleine Überschrift, darunter die Knöpfe. */
export const WerkzeugGruppe: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
    <div className="pdfx-gruppe" role="group" aria-label={titel}>
        <div className="pdfx-gruppe-titel" aria-hidden>
            {titel}
        </div>
        <div className="pdfx-knoepfe">{children}</div>
    </div>
);

/** Übrige Props gehen unverändert an den `<button>`. */
export type WerkzeugKnopfProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children" | "title"> & {
    icon: React.ReactElement;
    /** Kurze Beschriftung unter dem Symbol — zugleich der Name des Knopfs. */
    label: string;
    /** Erklärung beim Überfahren. */
    title?: string;
    /** Werkzeug ist gerade an — hervorgehoben, `aria-pressed`. Ohne Angabe kein Umschalter. */
    aktiv?: boolean;
    /** Öffnet ein Menü — kleines ▾ hinter der Beschriftung. */
    menue?: boolean;
};

/**
 * Kompakter Knopf der Werkzeugleiste: Symbol (20 px) über Text (11 px), kompakt nur das Symbol.
 * Leitet `ref` und alle übrigen Props an den `<button>` weiter.
 */
export const WerkzeugKnopf = React.forwardRef<HTMLButtonElement, WerkzeugKnopfProps>(
    ({ icon, label, title, aktiv, menue, className, type, ...rest }, ref) => {
        const kompakt = useWerkzeugKompakt();
        const pfeil = menue ? (
            <span className="pdfx-pfeil" aria-hidden>
                ▾
            </span>
        ) : null;
        return (
            <button
                ref={ref}
                type={type ?? "button"}
                {...rest}
                aria-pressed={aktiv === undefined ? undefined : aktiv}
                aria-label={kompakt ? label : undefined}
                title={kompakt ? (title ? `${label} — ${title}` : label) : title}
                className={["pdfx-wk", kompakt ? "kompakt" : "", className ?? ""].filter(Boolean).join(" ")}
            >
                {icon}
                {kompakt ? pfeil : (
                    <span className="pdfx-wk-text">
                        {label}
                        {pfeil}
                    </span>
                )}
            </button>
        );
    },
);
WerkzeugKnopf.displayName = "WerkzeugKnopf";

/** Knopf in der Schnellzugriffszeile (Rückgängig, Speichern, Schließen …). */
export const SchnellKnopf: React.FC<
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
        icon?: React.ReactElement;
        children?: React.ReactNode;
        primaer?: boolean;
    }
> = ({ icon, children, primaer, className, type, ...rest }) => (
    <button
        type={type ?? "button"}
        {...rest}
        className={["pdfx-sbtn", primaer ? "primary" : "", className ?? ""].filter(Boolean).join(" ")}
    >
        {icon}
        {children}
    </button>
);

/** Unter dieser Breite zeigt die Ansehen-Leiste nur Symbole — sie hat weniger Knöpfe als der Editor. */
export const ANSICHT_KOMPAKT_UNTER_PX = 760;

/**
 * Leiste über dem Dokument, solange es nur ANGESEHEN wird (User 04.10.2026:
 * „umdesignen in schön mit Platz und bitte Icons“). Dieselbe Formensprache wie
 * die `Werkzeugleiste` des Editors — Kopfzeile, darunter `WerkzeugGruppe`n mit
 * `WerkzeugKnopf`en —, aber ohne Reiter: zum Ansehen gibt es wenige Aktionen,
 * die alle gleichzeitig sichtbar sein dürfen.
 */
export const Ansichtsleiste: React.FC<{
    /** Nur Symbole zeigen? Ohne Angabe entscheidet die gemessene Breite der Leiste. */
    kompakt?: boolean;
    /** Kopfzeile links — Dateiname. */
    schnell?: React.ReactNode;
    /** Kopfzeile rechts — „Bearbeiten“. */
    rechts?: React.ReactNode;
    /** Eigene Zeile unter den Knöpfen — Fehler und Hinweise; leer nimmt sie keinen Platz. */
    hinweis?: React.ReactNode;
    /** Die Gruppen (`WerkzeugGruppe`). */
    children: React.ReactNode;
}> = ({ kompakt: kompaktVorgabe, schnell, rechts, hinweis, children }) => {
    const rahmenRef = React.useRef<HTMLDivElement | null>(null);
    const kompakt = useKompakt(rahmenRef, kompaktVorgabe, ANSICHT_KOMPAKT_UNTER_PX);
    return (
        <KompaktKontext.Provider value={kompakt}>
            <div ref={rahmenRef} className="pdfx pdfx-leiste pdfx-ansicht">
                <div className="pdfx-schnell" data-schnellzugriff="">
                    {schnell}
                    <span className="pdfx-spacer" />
                    {rechts}
                </div>
                <div className="pdfx-werkzeuge" role="toolbar" aria-label="Dokument ansehen">
                    {children}
                </div>
                <div className="pdfx-hinweis" data-werkzeug-hinweis="">{hinweis || null}</div>
            </div>
        </KompaktKontext.Provider>
    );
};
